
import { GoogleGenAI, Chat, Content, Modality } from "@google/genai";
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebaseConfig";
import { IkigaiBuckets, CareerPath, DashboardUpdatePayload, DaySimulation, SkillNode, DebateAnalysis, MirrorAnalysis, ValueAuctionAnalysis, GlobalOpportunityResult, SkillsGapResult, Message, KaiArchetype } from "../types";

// Client-Side Instance (Fallback/Dev)
let ai: GoogleGenAI | null = null;
let standardSession: Chat | null = null;
let timeCapsuleSession: Chat | null = null;
let originStorySession: Chat | null = null;
let activeMode: 'standard' | 'time_capsule' | 'origin_story' = 'standard';

// Runtime Flag: If prompt import fails (file deleted), we force cloud mode.
let forceCloudMode = false;

export const initializeGemini = () => {
    // Only initialize if we have a key
    if (process.env.API_KEY && !ai) {
        ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    }
};

/**
 * SECURITY: Check if client logic is stripped.
 * Returns true if we are in PRODUCTION (where local prompts are tree-shaken).
 * Returns false if we are in DEV (where local prompts are active).
 * Note: checks for safe access to import.meta.env to prevent runtime crash.
 */
export const isClientLogicSecure = (): boolean => {
    try {
        return !((import.meta as any).env && (import.meta as any).env.DEV);
    } catch {
        // If import.meta.env throws, implies we are in a restricted env (Prod), so Secure.
        return true;
    }
};

/**
 * HELPER: Clean chat history for transport (Client-Side SDK Format).
 */
const prepareHistoryForClient = (history: Message[]): Content[] => {
    const cleanHistory = history.filter(msg => !msg.isThinking && msg.text.trim() !== "");
    const truncatedHistory = cleanHistory.slice(-50);

    return truncatedHistory.map(msg => ({
        role: msg.role,
        parts: [{ text: msg.text }]
    }));
};

/**
 * HELPER: Clean chat history for transport (Cloud Function / Simple Format).
 */
const prepareHistoryForCloud = (history: Message[]) => {
    const cleanHistory = history.filter(msg => !msg.isThinking && msg.text.trim() !== "");
    const truncatedHistory = cleanHistory.slice(-50);

    return truncatedHistory.map(msg => ({
        role: msg.role,
        text: msg.text
    }));
};

// --- CONFIGURATION LOGIC ---
const getAIMode = (): 'client' | 'cloud' => {
    // 1. CRITICAL: If the prompts file is missing (Live Hosting), we MUST use cloud.
    if (forceCloudMode) {
        return 'cloud';
    }

    // 2. Check UI Toggle (Dev Preference)
    if (typeof window !== 'undefined') {
        const storedMode = localStorage.getItem('kai_ai_mode');
        if (storedMode === 'client' || storedMode === 'cloud') {
            return storedMode;
        }
    }

    // 3. Default to CLOUD (Development Default)
    // Only use client if explicitly requested or if we are forced to default.
    return 'cloud';
};

// --- SESSION MANAGEMENT ---

export const startNewChat = async (history?: Message[], archetype: KaiArchetype = 'sage', summaries: string[] = [], facts: string[] = [], buckets?: IkigaiBuckets) => {
    activeMode = 'standard';

    const targetMode = getAIMode();

    // Only attempt to init client session if requested
    if (targetMode === 'client') {
        initializeGemini();
        if (ai) {
            try {
                // SECURITY: Only load shared logic in DEV mode. 
                // In PROD, this block is tree-shaken, and we force cloud mode.
                if ((import.meta as any).env.DEV) {
                    const { getSystemInstruction } = await import('../../shared/prompts');
                    let finalInstruction = getSystemInstruction(archetype, facts, summaries, buckets);

                    standardSession = ai.chats.create({
                        model: 'gemini-2.5-flash',
                        history: history ? prepareHistoryForClient(history) : undefined,
                        config: { systemInstruction: finalInstruction, temperature: 0.7 },
                    });
                } else {
                    console.warn("🔒 Client Logic stripped in Production. Forcing Cloud Mode.");
                    forceCloudMode = true;
                    standardSession = null;
                }
            } catch (e) {
                console.warn("⚠️ Shared Prompts module not found. Forcing Cloud Mode.", e);
                forceCloudMode = true;
                standardSession = null;
            }
        }
    }
    return standardSession;
};

export const startTimeCapsuleChat = async (_buckets: IkigaiBuckets, history?: Message[]) => {
    activeMode = 'time_capsule';
    if (getAIMode() === 'client') {
        initializeGemini();
        if (ai) {
            try {
                if ((import.meta as any).env.DEV) {
                    const { getSystemInstruction } = await import('../../shared/prompts');
                    const futureYear = new Date().getFullYear() + 10;
                    const baseInstruction = getSystemInstruction('sage', [], [], _buckets);
                    const instruction = `ROLE PLAY: You are the user's Future Self (${futureYear})... \n\n ${baseInstruction}`;

                    timeCapsuleSession = ai.chats.create({
                        model: 'gemini-2.5-flash',
                        history: history ? prepareHistoryForClient(history) : undefined,
                        config: { systemInstruction: instruction, temperature: 0.9 }
                    });
                } else {
                    forceCloudMode = true;
                }
            } catch (e) {
                console.warn("⚠️ Client prompts missing. Forcing Cloud Mode.", e);
                forceCloudMode = true;
            }
        }
    }
    return timeCapsuleSession;
};

export const startOriginStoryChat = async (_buckets: IkigaiBuckets, history?: Message[]) => {
    activeMode = 'origin_story';
    if (getAIMode() === 'client') {
        initializeGemini();
        if (ai) {
            try {
                if ((import.meta as any).env.DEV) {
                    const { getSystemInstruction } = await import('../../shared/prompts');
                    const baseInstruction = getSystemInstruction('sage', [], [], _buckets);
                    const instruction = `ROLE PLAY: You are the user's Past Self (Age 8)... \n\n ${baseInstruction}`;

                    originStorySession = ai.chats.create({
                        model: 'gemini-2.5-flash',
                        history: history ? prepareHistoryForClient(history) : undefined,
                        config: { systemInstruction: instruction, temperature: 0.8 }
                    });
                } else {
                    forceCloudMode = true;
                }
            } catch (e) {
                console.warn("⚠️ Client prompts missing. Forcing Cloud Mode.", e);
                forceCloudMode = true;
            }
        }
    }
    return originStorySession;
};

export const switchToStandardChat = async (history?: Message[], archetype: KaiArchetype = 'sage', summaries: string[] = [], facts: string[] = [], buckets?: IkigaiBuckets) => {
    activeMode = 'standard';
    await startNewChat(history, archetype, summaries, facts, buckets);
};

// --- UNIFIED EXECUTION LAYER (The Hybrid Router) ---

export const sendMessageToKai = async (
    message: string,
    history: Message[],
    archetype: KaiArchetype,
    facts: string[],
    summaries: string[],
    buckets?: IkigaiBuckets
): Promise<string> => {

    // Check mode. Note: If startNewChat failed previously, forceCloudMode might already be true.
    let mode = getAIMode();

    // 1. CLOUD PATH (Default & Production Fallback)
    if (mode === 'cloud') {
        if (!functions) throw new Error("Firebase Functions not initialized. Cannot run in Cloud Mode.");

        try {
            console.log("🛡️ Routing message via Iron Dome (Cloud Functions)...");
            const simpleHistory = prepareHistoryForCloud(history);

            // Force cast functions to any or use non-null assertion if we are sure it's init
            const chatFn = httpsCallable(functions!, 'chat');
            const result = await chatFn({
                message,
                history: simpleHistory,
                archetype,
                facts,
                summaries,
                buckets,
                tools: { googleSearch: activeMode === 'standard', googleMaps: activeMode === 'standard' }
            });
            const data = result.data as any;
            return data.text || "";
        } catch (e: any) {
            console.error("Iron Dome Failed:", e);
            throw new Error(`Cloud Function Failed: ${e.message}`);
        }
    }

    // 2. CLIENT PATH (Dev Option)
    if (mode === 'client') {
        if (!process.env.API_KEY) {
            throw new Error("Client Mode requires API_KEY in env.");
        }

        try {
            console.log("⚡ Routing message via Client-Side SDK...");
            initializeGemini();

            // Get or Init Session
            let currentSession =
                activeMode === 'standard' ? standardSession :
                    activeMode === 'time_capsule' ? timeCapsuleSession :
                        originStorySession;

            // If session is missing or broken, try to initialize
            if (!currentSession) {
                const pastHistory = history.length > 0 ? history.slice(0, -1) : [];

                if (activeMode === 'standard') await startNewChat(pastHistory, archetype, summaries, facts, buckets);
                else if (activeMode === 'time_capsule') await startTimeCapsuleChat(buckets || {} as any, pastHistory);
                else await startOriginStoryChat(buckets || {} as any, pastHistory);

                // CRITICAL CHECK: Did initialization fail due to missing prompts?
                if (forceCloudMode) {
                    console.log("🔄 Client Init Failed. Rerouting to Cloud...");
                    return sendMessageToKai(message, history, archetype, facts, summaries, buckets);
                }

                currentSession = activeMode === 'standard' ? standardSession : (activeMode === 'time_capsule' ? timeCapsuleSession : originStorySession);
            }

            if (!currentSession) throw new Error("Failed to initialize Client Session");

            const result = await currentSession.sendMessageStream({ message });
            let fullText = "";
            for await (const chunk of result) {
                fullText += chunk.text;
            }
            return fullText;

        } catch (e: any) {
            console.error("Client-Side API failed.", e);
            throw e;
        }
    }

    return "Error: Unknown AI Mode";
};

// --- UTILS ---

export const parseKaiResponse = (fullText: string): { displayResponse: string; updates: DashboardUpdatePayload | null } => {
    const parts = fullText.split('|||');

    if (parts.length < 2) {
        return { displayResponse: fullText.trim(), updates: null };
    }

    const textPart = parts[0].trim();
    let jsonPart = parts[1].trim();
    jsonPart = jsonPart.replace(/^```json\s*/i, '').replace(/^```\s*/, '').replace(/```\s*$/, '').trim();

    try {
        const parsed = JSON.parse(jsonPart);
        if (parsed && parsed.update_dashboard) {
            return {
                displayResponse: textPart,
                updates: parsed.update_dashboard
            };
        }
    } catch (e) {
        console.warn("Failed to parse Kai's hidden JSON:", e);
    }

    return { displayResponse: textPart, updates: null };
};

export const extractImageFromResponse = (response: any): string | null => {
    if (response.candidates?.[0]?.content?.parts) {
        for (const part of response.candidates[0].content.parts) {
            if (part.inlineData?.data) {
                const mimeType = part.inlineData.mimeType || 'image/png';
                return `data:${mimeType};base64,${part.inlineData.data}`;
            }
        }
    }
    return null;
};

// --- GENERIC GENERATORS (Hybrid) ---

const runHybridGeneration = async (
    prompt: string,
    schema: any = null,
    modelName: string = 'gemini-2.5-flash',
    useTools: boolean = false
): Promise<any> => {

    const mode = getAIMode();

    // 1. Cloud Path
    if (mode === 'cloud') {
        try {
            if (!functions) throw new Error("Functions not initialized");
            console.log(`🛡️ Generating ${modelName} via Cloud...`);
            const genFn = httpsCallable(functions, 'generate');
            const res = await genFn({
                prompt,
                responseSchema: schema,
                responseMimeType: schema ? "application/json" : "text/plain",
                tools: useTools ? { googleSearch: true, googleMaps: true } : undefined
            });
            const data = res.data as any;

            if (schema) return JSON.parse(data.text);
            if (useTools) return { text: data.text, chunks: data.groundingMetadata?.groundingChunks || [] };
            return data.text;

        } catch (e) {
            console.error("Cloud generation failed.", e);
            throw e;
        }
    }

    // 2. Client Path
    if (mode === 'client' && process.env.API_KEY) {
        try {
            console.log(`⚡ Generating ${modelName} via Client...`);
            initializeGemini();
            if (ai) {
                const response = await ai.models.generateContent({
                    model: modelName,
                    contents: prompt,
                    config: {
                        responseMimeType: schema ? "application/json" : undefined,
                        responseSchema: schema,
                        tools: useTools ? [{ googleSearch: {} }, { googleMaps: {} }] : undefined
                    }
                });

                if (useTools) return { text: response.text, chunks: response.candidates?.[0]?.groundingMetadata?.groundingChunks || [] };
                return schema && response.text ? JSON.parse(response.text) : response.text;
            }
        } catch (e) {
            console.error("Client generation failed.", e);
            throw e;
        }
    }

    return null;
};

// --- FEATURE EXPORTS ---

export const summarizeMessages = async (messages: Message[]): Promise<string | null> => {
    const prompt = `Analyze the conversation... (Summary Prompt) \nTRANSCRIPT:\n${messages.map(m => m.text).join('\n')}`;
    try {
        const text = await runHybridGeneration(prompt);
        return text;
    } catch (e) { return null; }
};

export const generateCareerSuggestions = async (buckets: IkigaiBuckets): Promise<CareerPath[]> => {
    const prompt = `Analyze Ikigai data: ${JSON.stringify(buckets)}. 
    Generate 3 distinct "Future Prototypes" (Life Paths).
    These should NOT just be job titles. They can be lifestyles, creative pursuits, or hybrid roles.
    CRITICAL: For each path, you MUST identify exactly which items from the user's buckets were used as ingredients.`;

    const careerSchema = {
        type: "ARRAY",
        items: {
            type: "OBJECT",
            properties: {
                title: { type: "STRING" },
                description: { type: "STRING" },
                matchScore: { type: "INTEGER" },
                reasoning: { type: "ARRAY", items: { type: "STRING" } },
                bucketMatches: {
                    type: "OBJECT",
                    properties: {
                        passion: { type: "ARRAY", items: { type: "STRING" } },
                        mission: { type: "ARRAY", items: { type: "STRING" } },
                        profession: { type: "ARRAY", items: { type: "STRING" } },
                        vocation: { type: "ARRAY", items: { type: "STRING" } }
                    },
                    required: ["passion", "mission", "profession", "vocation"]
                }
            },
            required: ["title", "description", "matchScore", "reasoning", "bucketMatches"]
        }
    };
    return runHybridGeneration(prompt, careerSchema);
};

export const generateDaySimulation = async (careerTitle: string): Promise<DaySimulation | null> => {
    const prompt = `Generate a realistic "Day in the Life" simulation for: ${careerTitle}. Include tasks, environment, and social interactions.`;
    const simSchema = {
        type: "OBJECT",
        properties: {
            careerTitle: { type: "STRING" },
            schedule: { type: "ARRAY", items: { type: "OBJECT", properties: { time: { type: "STRING" }, activity: { type: "STRING" }, description: { type: "STRING" }, mood: { type: "STRING" } } } },
            summary: { type: "STRING" }
        }
    };
    return runHybridGeneration(prompt, simSchema);
};

export const generateSkillTree = async (careerTitle: string): Promise<SkillNode | null> => {
    const prompt = `Create a detailed Skill & Resource Tree for the path: "${careerTitle}".`;
    const skillSchema = {
        type: "OBJECT",
        properties: {
            id: { type: "STRING" },
            name: { type: "STRING" },
            category: { type: "STRING", enum: ["core", "hard", "soft"] },
            description: { type: "STRING" },
            status: { type: "STRING", enum: ["locked", "unlocked", "mastered"] },
            children: {
                type: "ARRAY",
                items: {
                    type: "OBJECT",
                    properties: {
                        id: { type: "STRING" },
                        name: { type: "STRING" },
                        category: { type: "STRING", enum: ["core", "hard", "soft"] },
                        description: { type: "STRING" },
                        status: { type: "STRING", enum: ["locked", "unlocked", "mastered"] },
                        children: {
                            type: "ARRAY",
                            items: {
                                type: "OBJECT",
                                properties: {
                                    id: { type: "STRING" },
                                    name: { type: "STRING" },
                                    category: { type: "STRING", enum: ["core", "hard", "soft"] },
                                    description: { type: "STRING" },
                                    status: { type: "STRING", enum: ["locked", "unlocked", "mastered"] },
                                    children: {
                                        type: "ARRAY",
                                        items: {
                                            type: "OBJECT",
                                            properties: {
                                                id: { type: "STRING" },
                                                name: { type: "STRING" },
                                                category: { type: "STRING", enum: ["core", "hard", "soft"] },
                                                description: { type: "STRING" },
                                                status: { type: "STRING", enum: ["locked", "unlocked", "mastered"] },
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        },
        required: ["id", "name", "category", "description", "children"]
    };
    return runHybridGeneration(prompt, skillSchema);
};

export const findGlobalOpportunities = async (careerTitle: string, mission: string[]): Promise<GlobalOpportunityResult | null> => {
    const prompt = `Find real-world hubs, cities, or communities for "${careerTitle}" specifically related to the mission: ${mission.join(',')}.`;
    return runHybridGeneration(prompt, null, 'gemini-2.5-flash', true);
};

export const bridgeSkillsGap = async (careerTitle: string, _currentSkills: string[], specificSkill?: string): Promise<SkillsGapResult | null> => {
    const prompt = `Find specific learning resources (courses, books, communities) for "${specificSkill || careerTitle}".`;
    return runHybridGeneration(prompt, null, 'gemini-2.5-flash', true);
};

export const generateVisionBoardImage = async (careerTitle: string, description: string): Promise<string | null> => {
    const mode = getAIMode();
    const prompt = `Photorealistic image representing the life of: ${careerTitle}. Context: ${description}. First-person or cinematic perspective. High quality, inspiring, warm lighting.`;

    let resultUrl: string | null = null;

    // 1. Cloud Path
    if (mode === 'cloud') {
        try {
            console.log("🛡️ Generating Vision via Cloud...");
            if (!functions) throw new Error("Functions not initialized");

            const visionFn = httpsCallable(functions, 'vision');
            const res = await visionFn({ careerTitle, description });
            resultUrl = (res.data as any).imageUrl;
        } catch (e: any) {
            console.error("Cloud vision failed", e);
            console.warn("⚠️ Cloud failed, falling back to Client-Side Vision...");
        }
    }

    // 2. Client Path (Also runs if Cloud fails and no URL was generated)
    if (!resultUrl && process.env.API_KEY) {
        try {
            console.log("⚡ Generating Vision via Client...");
            initializeGemini();
            if (ai) {
                const result = await ai.models.generateContent({
                    model: 'gemini-2.5-flash-image',
                    contents: { parts: [{ text: prompt }] }
                });
                resultUrl = extractImageFromResponse(result);
            }
        } catch (e) {
            console.warn("Client vision failed", e);
        }
    }

    return resultUrl;
};

export const generateMorningManifesto = async (buckets: IkigaiBuckets, quest: string | null): Promise<{ script: string, audioData: string } | null> => {
    const passionStr = buckets.passion.length > 0 ? buckets.passion.join(", ") : "discovery";
    const missionStr = buckets.mission.length > 0 ? buckets.mission.join(", ") : "making an impact";
    const questStr = quest || "taking the first step";

    const prompt = `Write a short, high-energy Morning Manifesto (max 40 words) for someone who loves ${passionStr} and wants to contribute to ${missionStr}. 
    Their specific goal today is: "${questStr}".
    Tone: Inspiring, punchy, direct. Second person ("You"). No formatting.`;

    const mode = getAIMode();

    // 1. Cloud Path
    if (mode === 'cloud') {
        try {
            console.log("🛡️ Generating TTS via Cloud...");
            const genFn = httpsCallable(functions!, 'generate');
            const scriptRes = await genFn({ prompt: prompt });
            const script = (scriptRes.data as any).text;

            const speakFn = httpsCallable(functions!, 'speak');
            const audioRes = await speakFn({ text: script });
            return { script, audioData: (audioRes.data as any).audioData };
        } catch (e) {
            console.warn("Cloud TTS failed", e);
        }
    }

    // 2. Client Path
    if (mode === 'client' && process.env.API_KEY) {
        try {
            console.log("⚡ Generating TTS via Client...");
            initializeGemini();
            if (ai) {
                const textRes = await ai.models.generateContent({
                    model: 'gemini-2.5-flash',
                    contents: [{ parts: [{ text: prompt }] }]
                });
                const script = textRes.text || "Rise and shine!";

                const audioRes = await ai.models.generateContent({
                    model: 'gemini-2.5-flash-preview-tts',
                    contents: [{ parts: [{ text: script }] }],
                    config: { responseModalities: [Modality.AUDIO], speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } } } }
                });
                const audioData = audioRes.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
                return audioData ? { script, audioData } : null;
            }
        } catch (e) {
            console.warn("Client TTS failed", e);
        }
    }

    return null;
};

export const analyzeValueAuction = async (allocation: any): Promise<ValueAuctionAnalysis | null> => {
    const prompt = `Analyze this Value Auction spending allocation (Budget $1000): ${JSON.stringify(allocation)}`;
    const auctionSchema = {
        type: "OBJECT",
        properties: {
            profile: { type: "STRING" },
            dominantValues: { type: "ARRAY", items: { type: "STRING" } },
            sacrifices: { type: "ARRAY", items: { type: "STRING" } },
            careerImplications: { type: "STRING" }
        },
        required: ["profile", "dominantValues", "sacrifices", "careerImplications"]
    };
    return runHybridGeneration(prompt, auctionSchema);
};

export const analyzeTextReflection = async (text: string): Promise<MirrorAnalysis | null> => {
    const prompt = `Analyze this user text (Resume/Journal/Bio) for Ikigai drivers: "${text.substring(0, 2000)}"`;
    const mirrorSchema = {
        type: "OBJECT",
        properties: {
            passion: { type: "ARRAY", items: { type: "STRING" } },
            mission: { type: "ARRAY", items: { type: "STRING" } },
            profession: { type: "ARRAY", items: { type: "STRING" } },
            vocation: { type: "ARRAY", items: { type: "STRING" } },
            blindSpots: { type: "ARRAY", items: { type: "STRING" } }
        },
        required: ["passion", "mission", "profession", "vocation", "blindSpots"]
    };
    return runHybridGeneration(prompt, mirrorSchema);
};

export const generateCrossroadsDebate = async (a: string, b: string): Promise<DebateAnalysis | null> => {
    const prompt = `Roleplay a debate between The Pragmatist vs The Visionary regarding: Option A: ${a}, Option B: ${b}`;
    const debateSchema = {
        type: "OBJECT",
        properties: {
            optionA: { type: "STRING" },
            optionB: { type: "STRING" },
            pragmatistArgument: { type: "STRING" },
            visionaryArgument: { type: "STRING" },
            blindSpot: { type: "STRING" },
            synthesis: { type: "STRING" },
            winner: { type: "STRING", enum: ["Option A", "Option B", "Hybrid"] }
        },
        required: ["optionA", "optionB", "pragmatistArgument", "visionaryArgument", "blindSpot", "synthesis", "winner"]
    };
    return runHybridGeneration(prompt, debateSchema);
};